import React from 'react';

// Calculates connector pixel positions on the shape
const getConnectorPosition = (side, position, width, height) => {
  const offsets = [0.2, 0.5, 0.8]; // 20%, 50%, 80% along each side
  const pct = offsets[position];

  switch (side) {
    case 'top':    return { x: width * pct, y: 0 };
    case 'bottom': return { x: width * pct, y: height };
    case 'left':   return { x: 0, y: height * pct };
    case 'right':  return { x: width, y: height * pct };
    default:       return { x: 0, y: 0 };
  }
};

const CONNECTOR_RADIUS = 6;
const CONNECTOR_RADIUS_FAV = 7;

const ShapePreview = ({
  width = 160,
  height = 100,
  frameColor = '#4ecca3',
  connectorColor = '#4ecca3',
  connectors = [],
  icon = '',
  name = '',
  onConnectorClick,
  interactive = false,
  scale = 1,
}) => {
  const pad = 30; // padding around shape for connectors
  const svgW = (width + pad * 2) * scale;
  const svgH = (height + pad * 2) * scale;

  return (
    <svg
      width={svgW}
      height={svgH}
      viewBox={`0 0 ${width + pad * 2} ${height + pad * 2}`}
      className="shape-preview-svg"
    >
      {/* Background grid dots */}
      <defs>
        <pattern id="previewGrid" width="16" height="16" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="0.5" fill="#1e2a4a" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#previewGrid)" rx="6" />

      {/* Shape rectangle */}
      <rect
        x={pad}
        y={pad}
        width={width}
        height={height}
        rx={6}
        fill="#16213e"
        stroke={frameColor}
        strokeWidth={2}
      />

      {/* Icon + name inside shape */}
      <text
        x={pad + width / 2}
        y={pad + height / 2 - 8}
        textAnchor="middle"
        fontSize="22"
        fill="white"
      >
        {icon}
      </text>
      <text
        x={pad + width / 2}
        y={pad + height / 2 + 14}
        textAnchor="middle"
        fontSize="10"
        fill="#8a9a95"
        fontFamily="Space Grotesk, sans-serif"
        fontWeight="500"
      >
        {name}
      </text>

      {/* Dimension labels */}
      <text
        x={pad + width / 2}
        y={pad + height + 20}
        textAnchor="middle"
        fontSize="9"
        fill="#4a5568"
        fontFamily="JetBrains Mono, monospace"
      >
        {width}px
      </text>
      <text
        x={pad - 14}
        y={pad + height / 2}
        textAnchor="middle"
        fontSize="9"
        fill="#4a5568"
        fontFamily="JetBrains Mono, monospace"
        transform={`rotate(-90, ${pad - 14}, ${pad + height / 2})`}
      >
        {height}px
      </text>

      {/* Connector points */}
      {connectors.map((c) => {
        const pos = getConnectorPosition(c.side, c.position, width, height);
        const cx = pad + pos.x;
        const cy = pad + pos.y;
        const r = c.favorite ? CONNECTOR_RADIUS_FAV : CONNECTOR_RADIUS;
        const isFav = c.favorite;
        const isEnabled = c.enabled;

        return (
          <g
            key={c.id}
            className={interactive ? 'connector-interactive' : ''}
            onClick={() => interactive && onConnectorClick && onConnectorClick(c.id)}
            style={{ cursor: interactive ? 'pointer' : 'default' }}
          >
            {/* Outer ring for favorites */}
            {isFav && (
              <circle
                cx={cx}
                cy={cy}
                r={r + 3}
                fill="none"
                stroke={connectorColor}
                strokeWidth={1.5}
                opacity={0.5}
              />
            )}
            {/* Main dot */}
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill={isEnabled ? connectorColor : '#2d3436'}
              stroke={isEnabled ? connectorColor : '#4a5568'}
              strokeWidth={isEnabled ? 0 : 1.5}
              opacity={isEnabled ? (isFav ? 1 : 0.6) : 0.3}
            />
            {/* Star marker for favorite */}
            {isFav && (
              <text
                x={cx}
                y={cy + 3.5}
                textAnchor="middle"
                fontSize="8"
                fill="#1a1a2e"
                fontWeight="700"
              >
                ★
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
};

export default ShapePreview;
